import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import lottie, { AnimationItem } from 'lottie-web'
import { isMobile } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { LoadingWrapper } from './styled'

interface Props {
  isCompleted: boolean
  onAnimationComplete?: (forceDismiss?: boolean) => void
  stopAnimation?: boolean
  minVisibleMs?: number
}

const FullScreenLoading = ({
  isCompleted,
  onAnimationComplete,
  stopAnimation,
  minVisibleMs = 7200,
}: Props) => {
  const [isLoadingMessageVisible, setIsLoadingMessageVisible] = useState(false)
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false)
  const [canDismiss, setCanDismiss] = useState(false)
  const [isForceDismiss, setIsForceDismiss] = useState(false)
  const animationRef = useRef<AnimationItem | null>(null)
  const loadSafetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const stopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mountedAtRef = useRef<number>(0)
  const stopHandledRef = useRef(false)
  const [isAnimationReady, setIsAnimationReady] = useState(false)
  const { t } = useTranslation()

  const finish = (forceDismiss = false) => {
    setIsForceDismiss(forceDismiss)
    setCanDismiss(true)
  }

  const clearAllTimers = () => {
    if (loadSafetyTimeoutRef.current) clearTimeout(loadSafetyTimeoutRef.current)
    if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current)
    loadSafetyTimeoutRef.current = null
    stopTimeoutRef.current = null
  }

  useLayoutEffect(() => {
    mountedAtRef.current = Date.now()
    stopHandledRef.current = false

    const element = document.getElementById('lottie')
    if (!element) {
      finish(true)
      return
    }

    const animation = lottie.loadAnimation({
      container: element,
      renderer: 'svg',
      loop: false,
      autoplay: true,
      path: `${process.env.PUBLIC_URL}/animation/motivum_loading.json`,
    })

    animationRef.current = animation
    setIsAnimationReady(true)
    clearAllTimers()

    loadSafetyTimeoutRef.current = setTimeout(() => finish(false), 15000)

    const handleComplete = () => {
      clearAllTimers()
      setHasPlayedOnce(true)
    }

    const handleError = () => {
      clearAllTimers()
      finish(true)
    }

    animation.addEventListener('complete', handleComplete)
    animation.addEventListener('data_failed', handleError)

    return () => {
      clearAllTimers()
      setIsAnimationReady(false)
      animation.removeEventListener('complete', handleComplete)
      animation.removeEventListener('data_failed', handleError)
      animation.destroy()
      animationRef.current = null
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setIsLoadingMessageVisible(true), 1000)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (stopAnimation && animationRef.current && isAnimationReady && !stopHandledRef.current) {
      stopHandledRef.current = true
      const elapsed = Date.now() - mountedAtRef.current
      const delay = Math.max(0, minVisibleMs - elapsed)

      clearAllTimers()
      stopTimeoutRef.current = setTimeout(() => {
        animationRef.current?.stop()
        setHasPlayedOnce(true)
        finish(true)
      }, delay)
    }
  }, [stopAnimation, isAnimationReady, minVisibleMs])

  useEffect(() => {
    if (isCompleted && hasPlayedOnce && !canDismiss) finish(false)
  }, [isCompleted, hasPlayedOnce, canDismiss])

  useEffect(() => {
    if (canDismiss) onAnimationComplete?.(isForceDismiss)
  }, [canDismiss, isForceDismiss, onAnimationComplete])

  const handleDoubleClick = () => {
    animationRef.current?.stop()
    clearAllTimers()
    finish(true)
  }

  return (
    <LoadingWrapper onDoubleClick={handleDoubleClick}>
      <div id="lottie" />
      <div className="content">
        <p className={isLoadingMessageVisible ? 'visible' : 'hidden'}>{t('loadingInProgress')}</p>
        {isMobile && window.innerHeight < 500 ? <p>{t('mobileNoticeToUseMonitor')}</p> : null}
        <div className={`progress-bar ${canDismiss ? 'completed' : ''}`}>
          <div className="progress" />
        </div>
      </div>
    </LoadingWrapper>
  )
}

export default FullScreenLoading
